// Iter 82.3 / Phase E1.3 — Employee Leave Portal.
// Personal landing page: balances, active/recent requests, upcoming
// holidays, pending clarifications. Powered by /api/leave/workspace/employee.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Warning, Confetti, ClockCounterClockwise } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const STATUS_TONE = {
    Draft: "bg-slate-100 text-slate-700",
    Pending: "bg-amber-100 text-amber-800",
    "Clarification Requested": "bg-sky-100 text-sky-800",
    Approved: "bg-emerald-100 text-emerald-800",
    Rejected: "bg-rose-100 text-rose-800",
    Cancelled: "bg-zinc-100 text-zinc-700",
    "Cancelled by HR": "bg-zinc-100 text-zinc-700",
};

export default function LeaveEmployeePortal() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data: payload } = await api.get("/leave/workspace/employee");
            setData(payload);
            setErr(null);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 404) {
                setErr("Your user account is not linked to an employee record. Ask HR to link your account to access the leave portal.");
            } else {
                toast.error(formatApiError(e, "Failed to load portal"));
                setErr("Could not load portal");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { Promise.resolve().then(reload); }, [reload]);

    if (loading) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="leave-portal-loading">
                <PageHeader eyebrow="Leave Operations" title="My Leave Portal" />
                <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>
            </div>
        );
    }
    if (err) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="leave-portal-error">
                <PageHeader eyebrow="Leave Operations" title="My Leave Portal" />
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex gap-2">
                    <Warning size={18} className="mt-0.5" /> <span>{err}</span>
                </div>
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="leave-portal-page">
            <PageHeader
                eyebrow={`Hello, ${data.employee.full_name}`}
                title="My Leave Portal"
                description={`Fiscal Year ${data.fiscal_year} · ${data.employee.department || ""} · ${data.employee.designation || ""}`}
                actions={(
                    <Link to="/leave/requests/new">
                        <Button data-testid="portal-apply-btn">
                            <CalendarPlus size={14} className="mr-1" /> Apply for Leave
                        </Button>
                    </Link>
                )}
            />

            {data.pending_clarifications.length > 0 && (
                <section className="mt-5 bg-sky-50 border border-sky-200 rounded-xl p-4" data-testid="portal-clarifications">
                    <h3 className="text-sm font-semibold text-sky-900 mb-2">
                        Pending clarifications from approvers ({data.pending_clarifications.length})
                    </h3>
                    <ul className="space-y-1">
                        {data.pending_clarifications.map((r) => (
                            <li key={r.id} className="text-xs">
                                <Link to={`/leave/requests/${r.id}`} className="text-sky-800 hover:underline" data-testid={`portal-clar-${r.id}`}>
                                    {r.leave_type_code} · {formatDate(r.from_date)} → {formatDate(r.to_date)} · {r.days_total} days
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portal-balances">
                {data.balances.length === 0 && data.balances_state && data.balances_state.code !== "ok" && (
                    <div
                        className={
                            "col-span-4 rounded-xl border p-5 " +
                            (data.balances_state.code === "fy_missing"
                                ? "bg-rose-50 border-rose-200 text-rose-900"
                                : data.balances_state.code === "policies_missing"
                                ? "bg-amber-50 border-amber-200 text-amber-900"
                                : "bg-sky-50 border-sky-200 text-sky-900")
                        }
                        data-testid={`portal-balances-state-${data.balances_state.code}`}
                    >
                        <div className="flex items-start gap-3">
                            <Warning size={20} className="mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <h4 className="text-sm font-semibold" data-testid="portal-balances-state-title">
                                    {data.balances_state.title || "No leave balances available"}
                                </h4>
                                <p className="text-xs mt-1" data-testid="portal-balances-state-message">
                                    {data.balances_state.message}
                                </p>
                                {data.balances_state.code === "not_provisioned"
                                    && Array.isArray(data.balances_state.expected_types)
                                    && data.balances_state.expected_types.length > 0 && (
                                    <div className="mt-3">
                                        <div className="text-[11px] uppercase tracking-wider font-semibold opacity-70">
                                            Policies awaiting provisioning
                                        </div>
                                        <ul className="mt-1 flex flex-wrap gap-1.5">
                                            {data.balances_state.expected_types.map((t) => (
                                                <li
                                                    key={t.code}
                                                    className="text-[11px] bg-white/60 border border-current/20 rounded-full px-2 py-0.5"
                                                    data-testid={`portal-balances-state-type-${t.code}`}
                                                >
                                                    <span className="font-mono font-semibold">{t.code}</span>
                                                    {t.policy_name && <span className="ml-1 opacity-80">· {t.policy_name}</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {data.balances.length === 0 && (!data.balances_state || data.balances_state.code === "ok") && (
                    <div className="col-span-4 text-center text-sm text-slate-400 py-8 bg-white rounded-xl border border-slate-200">
                        No leave balances configured for the current fiscal year. Contact HR if you expect access.
                    </div>
                )}
                {data.balances.map((b) => {
                    const opening = Number(b.opening_balance ?? 0);
                    const carryFwd = Number(b.carry_forward_in ?? 0);
                    const accrued = Number(b.total_accrued_ytd ?? 0);
                    const adjusted = Number(b.total_adjusted_ytd ?? 0);
                    const compOffCredited = Number(b.total_comp_off_credited_ytd ?? 0);
                    const taken = Number(b.total_taken_ytd ?? 0);
                    const encashed = Number(b.total_encashed_ytd ?? 0);
                    const expired = Number(b.total_expired_ytd ?? 0);
                    const compOffExpired = Number(b.total_comp_off_expired_ytd ?? 0);
                    const lop = Number(b.total_lop_ytd ?? 0);
                    // P0-A Batch 2 · three-value balance display.
                    // Ledger Balance | Reserved | Available.
                    const ledgerBalance = Number(b.current_balance ?? 0);
                    const reserved = Number(b.reserved_days ?? 0);
                    const available = Number(b.available_balance ?? ledgerBalance);
                    // Reconciliation invariant per the ledger bucketing in
                    // routes_leave_ledger.py::_balance_increment_for.
                    // Positive contributors: opening + carry_forward + accrued
                    //                      + adjusted + comp_off_credited
                    // Negative contributors: taken + encashed + expired
                    //                      + comp_off_expired + lop_charge
                    const computed = opening + carryFwd + accrued + adjusted + compOffCredited
                                   - taken - encashed - expired - compOffExpired - lop;
                    const drift = Number((ledgerBalance - computed).toFixed(4));
                    const hasDrift = Math.abs(drift) > 0.005;
                    return (
                    <div
                        key={b.leave_type_code}
                        className="bg-white border border-slate-200 rounded-xl p-4"
                        data-testid={`portal-balance-${b.leave_type_code}`}
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{b.leave_type_code}</div>
                                <div className="text-xs text-slate-700 mt-0.5">{b.policy_name || ""}</div>
                            </div>
                        </div>
                        {/* Three-value balance summary — Ledger | Reserved | Available */}
                        <div className="mt-3 grid grid-cols-3 gap-2">
                            <div className="text-center rounded bg-slate-50 py-2">
                                <div className="text-[9px] uppercase tracking-wider text-slate-500">Ledger</div>
                                <div className="text-lg font-semibold text-slate-800" data-testid={`portal-balance-${b.leave_type_code}-ledger`}>{ledgerBalance.toFixed(2)}</div>
                            </div>
                            <div className="text-center rounded bg-amber-50 py-2">
                                <div className="text-[9px] uppercase tracking-wider text-amber-700">Reserved</div>
                                <div className="text-lg font-semibold text-amber-900" data-testid={`portal-balance-${b.leave_type_code}-reserved`}>{reserved.toFixed(2)}</div>
                            </div>
                            <div className="text-center rounded bg-emerald-50 py-2">
                                <div className="text-[9px] uppercase tracking-wider text-emerald-700">Available</div>
                                <div className="text-lg font-bold text-emerald-900" data-testid={`portal-balance-${b.leave_type_code}-available`}>{available.toFixed(2)}</div>
                            </div>
                        </div>
                        {reserved > 0 && (
                            <div className="mt-2 text-[10px] text-slate-500 italic" title="Days held against pending / approved requests awaiting freeze">
                                {reserved.toFixed(2)}d held against pending/approved requests · authoritative Paid/LOP split determined at payroll freeze
                            </div>
                        )}
                        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 text-[11px] gap-x-2 gap-y-0.5">
                            {/* Always-shown positive contributors */}
                            <div className="text-slate-500">Opening Balance</div>
                            <div
                                className="text-right text-slate-800 font-medium"
                                data-testid={`portal-balance-${b.leave_type_code}-opening`}
                            >
                                {opening.toFixed(2)}
                            </div>
                            {/* Conditional positive contributors */}
                            {carryFwd > 0 && (
                                <>
                                    <div className="text-slate-500">Carry Forward In</div>
                                    <div
                                        className="text-right text-indigo-700"
                                        data-testid={`portal-balance-${b.leave_type_code}-carryfwd`}
                                    >
                                        {carryFwd.toFixed(2)}
                                    </div>
                                </>
                            )}
                            <div className="text-slate-500">Accrued YTD</div>
                            <div
                                className="text-right text-emerald-700"
                                data-testid={`portal-balance-${b.leave_type_code}-accrued`}
                            >
                                {accrued.toFixed(2)}
                            </div>
                            {compOffCredited > 0 && (
                                <>
                                    <div className="text-slate-500">Comp-Off Credited YTD</div>
                                    <div
                                        className="text-right text-emerald-700"
                                        data-testid={`portal-balance-${b.leave_type_code}-compoff-credited`}
                                    >
                                        {compOffCredited.toFixed(2)}
                                    </div>
                                </>
                            )}
                            {adjusted !== 0 && (
                                <>
                                    <div className="text-slate-500">Adjustments YTD</div>
                                    <div
                                        className={
                                            "text-right " +
                                            (adjusted >= 0 ? "text-emerald-700" : "text-amber-700")
                                        }
                                        data-testid={`portal-balance-${b.leave_type_code}-adjustments`}
                                    >
                                        {adjusted >= 0 ? "+" : ""}{adjusted.toFixed(2)}
                                    </div>
                                </>
                            )}
                            {/* Always-shown consumption */}
                            <div className="text-slate-500">Taken YTD</div>
                            <div
                                className="text-right text-rose-700"
                                data-testid={`portal-balance-${b.leave_type_code}-taken`}
                            >
                                {taken > 0 ? "−" : ""}{taken.toFixed(2)}
                            </div>
                            {/* Conditional consumption */}
                            {encashed > 0 && (
                                <>
                                    <div className="text-slate-500">Encashed YTD</div>
                                    <div
                                        className="text-right text-violet-700"
                                        data-testid={`portal-balance-${b.leave_type_code}-encashed`}
                                    >
                                        −{encashed.toFixed(2)}
                                    </div>
                                </>
                            )}
                            {expired > 0 && (
                                <>
                                    <div className="text-slate-500">Expired YTD</div>
                                    <div
                                        className="text-right text-orange-700"
                                        data-testid={`portal-balance-${b.leave_type_code}-expired`}
                                    >
                                        −{expired.toFixed(2)}
                                    </div>
                                </>
                            )}
                            {compOffExpired > 0 && (
                                <>
                                    <div className="text-slate-500">Comp-Off Expired YTD</div>
                                    <div
                                        className="text-right text-orange-700"
                                        data-testid={`portal-balance-${b.leave_type_code}-compoff-expired`}
                                    >
                                        −{compOffExpired.toFixed(2)}
                                    </div>
                                </>
                            )}
                            {lop > 0 && (
                                <>
                                    <div className="text-slate-500">LOP Charged YTD</div>
                                    <div
                                        className="text-right text-red-700"
                                        data-testid={`portal-balance-${b.leave_type_code}-lop`}
                                    >
                                        −{lop.toFixed(2)}
                                    </div>
                                </>
                            )}
                        </div>
                        {hasDrift && (
                            <div
                                className="mt-3 pt-2 border-t border-amber-200 flex items-center gap-1.5 text-[10px] text-amber-800 bg-amber-50 -mx-4 -mb-4 px-4 py-2 rounded-b-xl"
                                data-testid={`portal-balance-${b.leave_type_code}-reconciliation-drift`}
                                title={`Reconciliation drift · expected ${computed.toFixed(4)}, got ${ledgerBalance.toFixed(4)} · delta ${drift >= 0 ? "+" : ""}${drift.toFixed(4)} days`}
                            >
                                <Warning size={12} weight="fill" />
                                <span>Reconciliation drift · contact HR</span>
                            </div>
                        )}
                    </div>
                    );
                })}
            </section>

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <section className="lg:col-span-2 bg-white rounded-xl border border-slate-200" data-testid="portal-active-section">
                    <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <ClockCounterClockwise size={14} /> Active Requests ({data.active_requests.length})
                        </h3>
                        <Link to="/leave/requests?scope=mine" className="text-xs text-slate-500 hover:underline">View all my requests</Link>
                    </div>
                    {data.active_requests.length === 0 ? (
                        <div className="text-center py-8 text-sm text-slate-400">No active requests. Apply for leave when ready.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Leave Type</th>
                                    <th className="text-left px-3 py-2 font-medium">From → To</th>
                                    <th className="text-right px-3 py-2 font-medium">Days</th>
                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.active_requests.map((r) => (
                                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                        onClick={() => window.location.assign(`/leave/requests/${r.id}`)}
                                        data-testid={`portal-active-row-${r.id}`}>
                                        <td className="px-3 py-2 font-mono text-xs">{r.leave_type_code}</td>
                                        <td className="px-3 py-2 text-xs">{formatDate(r.from_date)} → {formatDate(r.to_date)}</td>
                                        <td className="px-3 py-2 text-right font-semibold">{Number(r.days_total).toFixed(1)}</td>
                                        <td className="px-3 py-2"><Badge className={`text-xs ${STATUS_TONE[r.status] || "bg-slate-100"}`}>{r.status}</Badge></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>

                <aside className="bg-white rounded-xl border border-slate-200" data-testid="portal-holidays">
                    <div className="px-5 py-3 border-b border-slate-100">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <Confetti size={14} /> Upcoming Holidays
                        </h3>
                    </div>
                    {data.upcoming_holidays.length === 0 ? (
                        <div className="px-5 py-6 text-xs text-slate-400 text-center">
                            No holidays in the next 30 days, or no calendar assigned.
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {data.upcoming_holidays.map((h) => (
                                <li key={h.date} className="px-5 py-2 text-xs flex justify-between" data-testid={`portal-holiday-${h.date}`}>
                                    <div className="font-medium text-slate-800">{h.name}</div>
                                    <div className="text-slate-500 font-mono">{formatDate(h.date)}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>
            </div>

            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="portal-recent-section">
                <div className="px-5 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-800">Recent Activity</h3>
                </div>
                {data.recent_requests.length === 0 ? (
                    <div className="text-center py-8 text-sm text-slate-400">No leave history yet.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Leave Type</th>
                                <th className="text-left px-3 py-2 font-medium">Period</th>
                                <th className="text-right px-3 py-2 font-medium">Days</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Submitted</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.recent_requests.map((r) => (
                                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => window.location.assign(`/leave/requests/${r.id}`)}
                                    data-testid={`portal-recent-row-${r.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{r.leave_type_code}</td>
                                    <td className="px-3 py-2 text-xs">{formatDate(r.from_date)} → {formatDate(r.to_date)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{Number(r.days_total).toFixed(1)}</td>
                                    <td className="px-3 py-2"><Badge className={`text-xs ${STATUS_TONE[r.status] || "bg-slate-100"}`}>{r.status}</Badge></td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{r.submitted_at ? formatDate(r.submitted_at) : formatDate(r.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}
